import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, api } from '../../lib/api';
import { Download, FileSpreadsheet, AlertTriangle, ShieldAlert, TrendingUp, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const inrCompact = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const EXEC_ROLES = new Set(['super_admin', 'md', 'ceo', 'cmd']);
const EXEC_DEPTS = new Set(['MD', 'CEO', 'CMD']);

export default function WorkOrderMISReport() {
  const { user } = useAuth() || {};
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const allowed = EXEC_ROLES.has(role) || EXEC_DEPTS.has(dept);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [gstRate, setGstRate] = useState(0.18);

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    apiGet('/reports/work-orders/mis', { gst_rate: gstRate })
      .then(setData)
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed to load MIS'))
      .finally(() => setLoading(false));
  }, [allowed, gstRate]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const r = await api.get('/reports/work-orders/mis/excel', { params: { gst_rate: gstRate }, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Work_Order_MIS_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!allowed) {
    return (
      <div>
        <PageHeader overline="Reports & MIS · Confidential" title="Work Order MIS Report" />
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-6 max-w-xl">
          <div className="flex items-start gap-3 text-rose-900">
            <ShieldAlert size={20} className="shrink-0" />
            <div>
              <div className="font-display font-bold mb-1">Restricted</div>
              <div className="text-sm">This report is accessible only to MD, CEO, CMD, and Super Admin. Contact administrator for access.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wo-mis-report">
      <PageHeader
        overline="Reports & MIS · Confidential"
        title="Work Order MIS Report"
        subtitle="Financial year-wise work order register · MD / CEO / CMD / Super Admin only · One click Excel export"
      />

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">GST Rate (%)</label>
            <input
              type="number"
              step="0.5"
              min="0"
              max="100"
              value={gstRate * 100}
              onChange={(e) => setGstRate(Math.max(0, Math.min(1, Number(e.target.value) / 100)))}
              className="w-24 px-3 py-1.5 border border-slate-300 rounded-sm text-sm"
              data-testid="mis-gst-rate"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-5 max-w-md">
            Applied to all WO basic values that don't carry an explicit `gst_rate` field. Default 18% (Standard EPC GST).
          </div>
        </div>
        <button
          onClick={downloadExcel}
          disabled={downloading || !data}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm font-display font-semibold disabled:opacity-50"
          data-testid="mis-download-excel"
        >
          {downloading ? (<><Loader2 size={14} className="animate-spin" /> Generating…</>) : (<><FileSpreadsheet size={14} /> Download Excel</>)}
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500">Loading report…</div>
      ) : !data ? (
        <div className="p-8 text-center text-slate-500">No data available</div>
      ) : (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KPI label="Total WOs" value={data.totals.count} icon={TrendingUp} />
            <KPI label="Basic Value" value={inrCompact(data.totals.basic_amount)} />
            <KPI label="GST" value={inrCompact(data.totals.gst)} />
            <KPI label="Grand Total" value={inrCompact(data.totals.grand_total)} accent />
          </div>

          {/* FY Summary table */}
          {data.summary.length > 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm mb-5 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 font-display font-bold text-sm uppercase tracking-wider text-slate-700">FY Summary</div>
              <table className="w-full text-sm" data-testid="mis-fy-summary">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Financial Year</th>
                    <th className="text-right px-4 py-2">WO Count</th>
                    <th className="text-right px-4 py-2">Basic</th>
                    <th className="text-right px-4 py-2">GST</th>
                    <th className="text-right px-4 py-2">Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.summary.map(s => (
                    <tr key={s.fy} className="border-t border-slate-100 hover:bg-indigo-50/40">
                      <td className="px-4 py-2 font-display font-semibold text-[#3b4872]">{s.fy}</td>
                      <td className="px-4 py-2 text-right">{s.count}</td>
                      <td className="px-4 py-2 text-right font-mono">{inrCompact(s.basic_amount)}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-600">{inrCompact(s.gst)}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold">{inrCompact(s.grand_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-5 flex items-start gap-2 text-sm">
              <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
              <div>
                <div className="font-display font-bold text-amber-900">No work orders yet</div>
                <div className="text-amber-800 text-xs">Add Work Orders to projects via Project Detail → Work Orders tab. They'll appear here grouped by FY based on start date.</div>
              </div>
            </div>
          )}

          {/* Full table */}
          {data.rows.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 font-display font-bold text-sm uppercase tracking-wider text-slate-700">All Work Orders</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="mis-wo-table">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Site Code</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">WO Number</th>
                      <th className="text-left px-3 py-2">Client</th>
                      <th className="text-left px-3 py-2">Nature of Work</th>
                      <th className="text-right px-3 py-2">Basic (₹)</th>
                      <th className="text-right px-3 py-2">GST (₹)</th>
                      <th className="text-right px-3 py-2">Grand Total (₹)</th>
                      <th className="text-left px-3 py-2">FY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={`${r.wo_number}-${i}`} className="border-t border-slate-100 hover:bg-indigo-50/30">
                        <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.site_code}</td>
                        <td className="px-3 py-1.5 text-xs">{r.date}</td>
                        <td className="px-3 py-1.5 font-mono text-xs">{r.wo_number}</td>
                        <td className="px-3 py-1.5">{r.client_name}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-600 max-w-xs truncate" title={r.nature_of_work}>{r.nature_of_work}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{r.basic_amount.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-slate-600">{r.gst.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-bold">{r.grand_total.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-1.5 text-xs"><span className="px-1.5 py-0.5 bg-indigo-50 text-[#3b4872] rounded-sm font-display font-semibold">{r.fy}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent }) {
  return (
    <div className={`bg-white border rounded-sm p-4 ${accent ? 'border-[#3b4872]' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon size={14} className="text-slate-400" />}
      </div>
      <div className={`mt-1 text-xl font-display font-extrabold ${accent ? 'text-[#3b4872]' : 'text-slate-900'}`}>{value}</div>
    </div>
  );
}
